import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Slider, Arrow } from '@tc-packages/tc-common'

const MAX_STATIC_CARDS = 3

function ArticleCard({ item, isSlide }) {
  const { description, image_alt, image_url, link_text, title, url } = item
  const CardWrapper = url ? 'a' : 'div'

  return (
    <div
      className={[
        'basicCards_item',
        !isSlide && 'col-md-4',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <div className="basicCards_block">
        <CardWrapper className="basicCards_blockLink" href={url || undefined}>
          <div
            aria-label={image_alt}
            className="basicCards_img"
            role="img"
            style={{ backgroundImage: `url(${image_url})` }}
          />
          <div className="basicCards_content">
            <h4 className="basicCards_title">{title}</h4>
            {description && <div className="basicCards_text">{description}</div>}
          </div>
          {url && link_text && (
            <span className="basicCards_link readMoreLink">{link_text}</span>
          )}
        </CardWrapper>
      </div>
    </div>
  )
}

ArticleCard.propTypes = {
  isSlide: PropTypes.bool.isRequired,
  item: PropTypes.shape({
    description: PropTypes.string,
    image_alt: PropTypes.string,
    image_url: PropTypes.string.isRequired,
    link_text: PropTypes.string,
    title: PropTypes.string.isRequired,
    url: PropTypes.string,
  }).isRequired,
}

export default function ArticleCardsSection({ section, translations = {} }) {
  const { items = [], title } = section

  const [initialSlides, setInitialSlides] = useState(MAX_STATIC_CARDS)
  const [showAll, setShowAll] = useState(false)

  useEffect(() => {
    const updateSlidesToShow = () => {
      let currentSlides = MAX_STATIC_CARDS

      if (window.matchMedia('(max-width: 1024px)').matches) {
        currentSlides = 2
      }
      if (window.matchMedia('(max-width: 767px)').matches) {
        currentSlides = 1
      }

      setInitialSlides(currentSlides)
    }

    updateSlidesToShow()
    window.addEventListener('resize', updateSlidesToShow)
    return () => window.removeEventListener('resize', updateSlidesToShow)
  }, [])

  const cardLinkStyles = {
    '--card-link-background': section.card_link_background,
    '--card-link-background-hover': section.card_link_background_hover,
    '--card-link-color': section.card_link_color,
    '--card-link-color-hover': section.card_link_color_hover,
  }

  const carouselControlsButtonStyles = {
    '--button-carousel-controls-background':
      section.button_carousel_controls_background,
    '--button-carousel-controls-background-hover':
      section.button_carousel_controls_background_hover,
    '--button-carousel-controls-color': section.button_carousel_controls_color,
    '--button-carousel-controls-color-hover':
      section.button_carousel_controls_color_hover,
  }

  const carouselSettings = {
    className: 'articleCards_slider',
    infinite: false,
    nextArrow: (
      <Arrow
        carouselControlsButtonStyles={carouselControlsButtonStyles}
        direction="next"
      />
    ),
    prevArrow: (
      <Arrow
        carouselControlsButtonStyles={carouselControlsButtonStyles}
        direction="prev"
      />
    ),
    slidesToScroll: 1,
    slidesToShow: initialSlides,
  }

  if (items.length === 0) {
    return null
  }

  const isMobile = initialSlides === 1
  const hasMoreItems = items.length > MAX_STATIC_CARDS
  const showCarousel = !isMobile && hasMoreItems
  const canToggle = isMobile && hasMoreItems
  const visibleItems = canToggle && !showAll ? items.slice(0, MAX_STATIC_CARDS) : items

  return (
    <section className="articleCards" style={cardLinkStyles}>
      {title && title.trim() !== '' && (
        <div className="sectionTitle--centered">
          <div className="sectionTitle--centered_container container">
            <div className="sectionTitle--centered_contentWrapper contentWrapper_small">
              <h3 className="sectionTitle--centered_title">{title}</h3>
            </div>
          </div>
        </div>
      )}
      <div className="container">
        {showCarousel ? (
          <Slider
            items={items.map((item) => (
              <ArticleCard isSlide item={item} key={item.index} />
            ))}
            {...carouselSettings}
          />
        ) : (
          <div className={isMobile ? 'basicCards_wrapp basicCards_one-row-mobile' : undefined}>
            <div className="basicCards_row row justify-content-center">
              {visibleItems.map((item) => (
                <ArticleCard isSlide={false} item={item} key={item.index} />
              ))}
            </div>
            {canToggle && (
              <div className="articleCards_toggle">
                <button
                  className="articleCards_toggleBtn"
                  onClick={() => setShowAll((prev) => !prev)}
                  type="button"
                >
                  {showAll
                    ? translations.show_less || 'Show less'
                    : translations.show_more || 'Show more...'}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  )
}

ArticleCardsSection.propTypes = {
  section: PropTypes.shape({
    button_carousel_controls_background: PropTypes.string,
    button_carousel_controls_background_hover: PropTypes.string,
    button_carousel_controls_color: PropTypes.string,
    button_carousel_controls_color_hover: PropTypes.string,
    card_link_background: PropTypes.string,
    card_link_background_hover: PropTypes.string,
    card_link_color: PropTypes.string,
    card_link_color_hover: PropTypes.string,
    items: PropTypes.array.isRequired,
    node_id: PropTypes.string.isRequired,
    title: PropTypes.string,
  }).isRequired,
  translations: PropTypes.shape({
    show_less: PropTypes.string,
    show_more: PropTypes.string,
  }),
}
